import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";
import { FAKE_CLOCK_PRELUDE } from "./fake-clock.ts";

const starter = `type Debounced<A extends unknown[]> = {
  (...args: A): void;
  cancel(): void;
  flush(): void;
};

function debounce<A extends unknown[]>(fn: (...args: A) => void, wait: number): Debounced<A> {
  const wrapped = (..._args: A) => {
    // TODO: schedule fn to run \`wait\` ms after the LAST call
  };
  wrapped.cancel = () => {
    // TODO: drop anything pending
  };
  wrapped.flush = () => {
    // TODO: run anything pending right now
  };
  return wrapped as Debounced<A>;
}
`;

const solution = `type Debounced<A extends unknown[]> = {
  (...args: A): void;
  cancel(): void;
  flush(): void;
};

function debounce<A extends unknown[]>(fn: (...args: A) => void, wait: number): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  // The most recent arguments are kept, not the first: a debounce fires with
  // what the user last typed, and flush has to use them too.
  let pending: A | null = null;

  const run = () => {
    const args = pending as A;
    timer = null;
    pending = null;
    fn(...args);
  };

  const wrapped = (...args: A) => {
    pending = args;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(run, wait);
  };

  wrapped.cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    pending = null;
  };

  wrapped.flush = () => {
    // No-op when nothing is pending, so flush on an idle debounce cannot fire a
    // stale call. Clearing the timer matters as much as calling fn: without it
    // the scheduled run still arrives later and fires a second time.
    if (timer === null) return;
    clearTimeout(timer);
    run();
  };

  return wrapped as Debounced<A>;
}`;

const harness = HARNESS_KIT + `
if (typeof debounce !== "function") {
  throw new Error("debounce is not defined. Keep the function name as given.");
}

function __spy() {
  const s = { calls: [] };
  s.fn = function () { s.calls.push(Array.prototype.slice.call(arguments)); };
  return s;
}

const __tests = [
  {
    name: "does not fire immediately",
    about: "A debounce is trailing-edge: nothing happens until the calls stop.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("a");
      __expectExact("debounce(fn,100); call once, advance 0", function () { return s.calls.length; }, 0);
    }
  },
  {
    name: "fires once the wait has elapsed",
    about: "After the quiet period, the call goes through with its arguments.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("a");
      __clock.advance(100);
      __expectExact("call once, advance 100", function () { return s.calls; }, [["a"]]);
    }
  },
  {
    name: "a burst of calls produces exactly one",
    about: "The reason debounce exists: collapse a burst into a single trailing call.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d(1); d(2); d(3);
      __clock.advance(100);
      __expectExact("three calls then advance 100", function () { return s.calls.length; }, 1);
    }
  },
  {
    name: "fires with the most recent arguments",
    about: "A search box must query what was typed last, not first.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("re"); d("rea"); d("react");
      __clock.advance(100);
      __expectExact("typed re, rea, react", function () { return s.calls; }, [["react"]]);
    }
  },
  {
    name: "each call restarts the timer",
    hidden: true,
    about: "This is what separates debounce from throttle. If the timer is only set on the first call, it fires while the user is still typing.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("a");
      __clock.advance(90);
      d("b");
      __clock.advance(90);
      __expectExact("call, +90ms, call, +90ms (180ms total)", function () { return s.calls.length; }, 0);
      __clock.advance(10);
      __expectExact("... then +10ms more", function () { return s.calls; }, [["b"]]);
    }
  },
  {
    name: "cancel drops the pending call entirely",
    hidden: true,
    about: "Cleanup on unmount. The call must never arrive, no matter how much time passes.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("a");
      d.cancel();
      __clock.advance(1000);
      __expectExact("call then cancel then advance 1000", function () { return s.calls.length; }, 0);
    }
  },
  {
    name: "flush runs the pending call immediately, with the last arguments",
    hidden: true,
    about: "Submitting a form should not wait out the debounce. The arguments are the trap: flushing with the FIRST call's arguments is the common slip.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("first"); d("last");
      d.flush();
      __expectExact("two calls then flush", function () { return s.calls; }, [["last"]]);
    }
  },
  {
    name: "flush does not leave the timer to fire again",
    hidden: true,
    about: "Calling fn without clearing the timer means the scheduled run still arrives and fires a second time. Everything looks right until you advance the clock.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("a");
      d.flush();
      __clock.advance(1000);
      __expectExact("flush then advance 1000", function () { return s.calls.length; }, 1);
    }
  },
  {
    name: "flush with nothing pending does nothing",
    hidden: true,
    about: "Flushing an idle debounce must not fire a stale call from ten minutes ago.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d.flush();
      d("a");
      __clock.advance(100);
      d.flush();
      __expectExact("flush idle, call, advance, flush again", function () { return s.calls.length; }, 1);
    }
  },
  {
    name: "cancel after flush is harmless",
    hidden: true,
    about: "Both are cleanup calls and both can run in either order.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("a");
      d.flush();
      d.cancel();
      __clock.advance(1000);
      __expectExact("flush then cancel", function () { return s.calls.length; }, 1);
    }
  },
  {
    name: "usable again after cancel",
    hidden: true,
    about: "Cancel drops the pending call, it does not permanently disable the debounce.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("a");
      d.cancel();
      d("b");
      __clock.advance(100);
      __expectExact("cancel then call again", function () { return s.calls; }, [["b"]]);
    }
  },
  {
    name: "separate bursts each fire",
    hidden: true,
    about: "After firing, the debounce resets. A one-shot guard would swallow every later burst.",
    run: function () {
      const s = __spy();
      const d = debounce(s.fn, 100);
      d("one");
      __clock.advance(100);
      d("two");
      __clock.advance(100);
      __expectExact("two separate bursts", function () { return s.calls; }, [["one"], ["two"]]);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "Two things have to survive between calls: the timer handle and the arguments. Start by naming them, then ask what each of cancel and flush is supposed to do with each one.",
    code: `function debounce<A extends unknown[]>(fn: (...args: A) => void, wait: number) {
  // The two things that have to outlive any single call:
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: A | null = null;

  // ... the wrapper, cancel and flush all close over exactly these two
}`,
  },
  {
    label: "Approach",
    body: "One closure. Every call stores the newest arguments, clears the pending timer and schedules a fresh one, which is what makes each call restart the clock. Cancel clears the timer and forgets the arguments. That leaves only flush to write.",
    code: `const run = () => {
  const args = pending as A;
  timer = null;
  pending = null;
  fn(...args);
};

const wrapped = (...args: A) => {
  pending = args;                              // newest arguments win
  if (timer !== null) clearTimeout(timer);     // each call restarts the clock
  timer = setTimeout(run, wait);
};

wrapped.cancel = () => {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  pending = null;
};

// flush is the only one left, and it is the one that bites.`,
  },
  {
    label: "The trap",
    body: "flush runs with the last arguments, not the first, and it must clear the pending timer as well as call fn. Miss the clearTimeout and the scheduled run still arrives afterwards, so the function fires twice. Returning early when nothing is pending is what stops flush on an idle debounce firing a stale call.",
  },
];

export const debounceDrill: Drill = {
  id: "debounce",
  track: "typescript",
  title: "Debounce with cancel and flush",
  pattern: "timers-closures",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  prelude: FAKE_CLOCK_PRELUDE,
  statement: `A search box fires a request on every keystroke. Write \`debounce\`, which wraps a function so it runs only once the calls stop, plus the two controls every real implementation needs.

\`\`\`ts
function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  wait: number,
): {
  (...args: A): void;
  cancel(): void;   // drop anything pending
  flush(): void;    // run anything pending, now
};
\`\`\`

## Requirements

- **Trailing edge.** Nothing runs until \`wait\` milliseconds have passed with no further calls.
- **Each call restarts the clock.**
- **Fires with the most recent arguments.**
- **\`cancel\`** drops the pending call so it never happens.
- **\`flush\`** runs it immediately, and does nothing if nothing is pending.
- Both are safe to call repeatedly and in any order, and the debounce stays usable afterwards.

## Example

\`\`\`ts
const search = debounce((q: string) => fetch("/search?q=" + q), 300);

search("re");
search("rea");
search("react");   // 300ms after this one, and only this one, fetch runs with "react"

// on submit, do not make the user wait out the delay:
search.flush();

// on unmount:
search.cancel();
\`\`\`

## Where this gets interesting

\`flush\` is where most implementations are subtly wrong in two ways at once. It has to run with the **last** arguments, not the first. And it has to **clear the pending timer**, or the scheduled call still arrives afterwards and the function runs twice. Both look fine until someone advances the clock after flushing.

\`setTimeout\`, \`clearTimeout\` and \`Date.now\` are driven by a fake clock in this drill, so tests are instant and deterministic. Write it exactly as you normally would.`,
  hints,
  starter,
  harness,
  solution,
  complexity: "O(1) per call. One timer and one slot for the pending arguments, regardless of burst size.",
};
